import React, { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, XCircle, Clock, HelpCircle, ArrowRight } from 'lucide-react';
import { classifyReturnStatus } from '@/components/payments/payuCheckout';
import { buildPayuTabUrl } from './payuTabs';

/**
 * Generic payment-return landing page. PayU (and eventually other gateways
 * using the same POST-redirect pattern) bounces the customer here after
 * the hosted checkout completes.
 *
 * CRITICAL: this page shows UX only — authoritative payment state comes
 * from the server-to-server webhook, not these query params. A malicious
 * user can hit /payment/return?status=success manually and see this
 * success UI; that's fine for UX, but downstream business logic (fulfill
 * order, unlock feature, etc.) must read the DB row that the webhook
 * wrote, never trust the URL.
 */
const PaymentReturn: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const gateway = params.get('gateway') || 'unknown';
  const txnid = params.get('txnid') || '';
  const amount = params.get('amount') || '';
  const rawStatus = params.get('status') || '';
  const classification = useMemo(() => classifyReturnStatus(rawStatus), [rawStatus]);

  const copy = (() => {
    switch (classification) {
      case 'success':
        return {
          icon: <CheckCircle2 className="h-14 w-14 text-green-600" />,
          title: 'Payment received',
          body: 'Thanks — we\'re confirming the transaction with your bank. Your account will reflect the update shortly.',
          testId: 'payment-return-success',
        };
      case 'failure':
        return {
          icon: <XCircle className="h-14 w-14 text-red-600" />,
          title: 'Payment did not go through',
          body: 'The payment was not completed. No money has been deducted. You can safely try again.',
          testId: 'payment-return-failure',
        };
      case 'pending':
        return {
          icon: <Clock className="h-14 w-14 text-amber-600" />,
          title: 'Payment is processing',
          body: 'Your bank is still confirming the transaction. This can take a few minutes. We\'ll update your account once we hear back.',
          testId: 'payment-return-pending',
        };
      default:
        return {
          icon: <HelpCircle className="h-14 w-14 text-slate-500" />,
          title: 'Payment status unclear',
          body: 'We couldn\'t determine the payment status from the return URL. Check your billing page or try again.',
          testId: 'payment-return-unknown',
        };
    }
  })();

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-b from-slate-50 to-slate-100">
      <Card className="max-w-md w-full" data-testid={copy.testId}>
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">{copy.icon}</div>
          <CardTitle className="text-2xl">{copy.title}</CardTitle>
          <CardDescription>{copy.body}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {(txnid || amount) && (
            <div className="rounded-lg border bg-slate-50 p-4 text-sm space-y-2">
              {txnid && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Transaction ID</span>
                  <span className="font-mono text-xs" data-testid="payment-return-txnid">{txnid}</span>
                </div>
              )}
              {amount && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Amount</span>
                  <span className="font-medium">₹{amount}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Gateway</span>
                <span className="capitalize">{gateway}</span>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => navigate('/home')}>
              Dashboard
            </Button>
            <Button
              className="flex-1 gap-2"
              onClick={() => navigate(gateway === 'payu' ? buildPayuTabUrl('payments') : '/home/billing')}
            >
              View payments <ArrowRight className="h-4 w-4" />
            </Button>
          </div>

          <p className="text-xs text-center text-muted-foreground pt-2">
            Final payment status is confirmed by our server. If it doesn't reflect within a few minutes, refresh the payments page.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentReturn;
